import React, { useState, useEffect } from 'react';
import {
  BarChart3, MessageSquare, CalendarDays, Search, User, Mail, Phone,
  Reply, Archive, X, CheckCircle, XCircle, AlertCircle, Loader2, Clock, MapPin
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import AnalyticsPanel from '../components/AnalyticsPanel';
import { adminApi } from '../context/AdminAuthContext';

const T = { vert: '#1e4a1a', or: '#c4a76c', textPrimary: '#1a1a1a', textSecondary: '#6b7280', textMuted: '#9ca3af', border: '#e8e5e0' };
const CARD = { background: '#ffffff', border: `1px solid ${T.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const INPUT_STYLE = { background: '#f5f3ef', border: `1px solid ${T.border}`, color: T.textPrimary, fontFamily: "'Montserrat', sans-serif" };

const TABS = [
  { id: 'stats', label: 'Statistiques', icon: BarChart3 },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'bookings', label: 'Reservations', icon: CalendarDays },
];

const formatDate = (d) => {
  try { return new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (_) { return d; }
};

const AdminAudiencePage = () => {
  const [activeTab, setActiveTab] = useState('stats');

  return (
    <AdminLayout>
      <div className="max-w-7xl" data-testid="admin-audience-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Audience & Visites</h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Analytics, messages et reservations</p>
        </div>

        <div className="flex gap-1 mb-8 p-1 rounded-xl" style={{ background: '#f0ede8' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`audience-tab-${tab.id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-medium transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: activeTab === tab.id ? '#ffffff' : 'transparent', color: activeTab === tab.id ? T.vert : T.textMuted, boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'stats' && <AnalyticsPanel />}
        {activeTab === 'messages' && <MessagesTab />}
        {activeTab === 'bookings' && <BookingsTab />}
      </div>
    </AdminLayout>
  );
};

const MessagesTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { load(); }, [filter]);
  const load = async () => { setLoading(true); try { setContacts(await adminApi.get(filter === 'all' ? '/api/admin/contacts' : `/api/admin/contacts?status=${filter}`)); } catch (err) { console.error('Failed to load contacts:', err); } setLoading(false); };
  const updateStatus = async (id, status) => { setUpdating(true); try { await adminApi.patch(`/api/admin/contacts/${id}`, { status }); await load(); if (selected?.contact_id === id) setSelected({ ...selected, status }); } catch (err) { console.error('Failed to update status:', err); } setUpdating(false); };

  const badge = (status) => {
    const m = { new: { l: 'Nouveau', c: '#2563eb', b: 'rgba(59,130,246,0.08)' }, read: { l: 'Lu', c: T.textMuted, b: '#f3f4f6' }, replied: { l: 'Repondu', c: '#16a34a', b: 'rgba(22,163,74,0.08)' }, archived: { l: 'Archive', c: T.textMuted, b: '#f3f4f6' } };
    const s = m[status] || m.new;
    return <span className="px-2.5 py-1 text-[10px] font-semibold rounded-full" style={{ backgroundColor: s.b, color: s.c }}>{s.l}</span>;
  };

  const filtered = contacts.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase()) || c.subject.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="messages-tab">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.textMuted }} />
          <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'new', 'read', 'replied', 'archived'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="px-4 py-2.5 text-[12px] font-medium rounded-xl transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: filter === s ? 'rgba(30,74,26,0.08)' : '#f5f3ef', color: filter === s ? T.vert : T.textMuted }}>
              {s === 'all' ? 'Tous' : s === 'new' ? 'Nouveaux' : s === 'read' ? 'Lus' : s === 'replied' ? 'Repondus' : 'Archives'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white" style={CARD}>
          <MessageSquare className="w-10 h-10 mx-auto mb-3" style={{ color: T.border }} />
          <p style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>Aucun message</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => (
            <div key={c.contact_id} onClick={() => { setSelected(c); if (c.status === 'new') updateStatus(c.contact_id, 'read'); }}
              className="rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md bg-white" style={{ ...CARD, borderColor: c.status === 'new' ? 'rgba(59,130,246,0.2)' : T.border }}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1.5">
                    <p className="font-medium text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{c.name}</p>
                    {badge(c.status)}
                  </div>
                  <p className="font-medium text-sm mb-1" style={{ color: T.textSecondary }}>{c.subject}</p>
                  <p className="text-[12px] line-clamp-1" style={{ color: T.textMuted }}>{c.message}</p>
                </div>
                <span className="text-[11px] whitespace-nowrap" style={{ color: T.textMuted }}>{formatDate(c.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl overflow-y-auto max-h-[90vh] bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Message</h2>
              <button onClick={() => setSelected(null)} style={{ color: T.textMuted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">{badge(selected.status)}</div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-3"><User className="w-4 h-4" style={{ color: T.textMuted }} /><span className="text-sm" style={{ color: T.textPrimary }}>{selected.name}</span></div>
                <div className="flex items-center gap-3"><Mail className="w-4 h-4" style={{ color: T.textMuted }} /><a href={`mailto:${selected.email}`} className="text-sm" style={{ color: T.vert }}>{selected.email}</a></div>
                {selected.phone && <div className="flex items-center gap-3"><Phone className="w-4 h-4" style={{ color: T.textMuted }} /><a href={`tel:${selected.phone}`} className="text-sm" style={{ color: T.vert }}>{selected.phone}</a></div>}
              </div>
              <div><p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: T.textMuted }}>Sujet</p><p className="font-medium text-sm" style={{ color: T.textPrimary }}>{selected.subject}</p></div>
              <div><p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: T.textMuted }}>Message</p><p className="text-sm p-4 rounded-xl whitespace-pre-wrap" style={{ background: '#f5f3ef', color: T.textPrimary }}>{selected.message}</p></div>
              <p className="text-[11px]" style={{ color: T.textMuted }}>Recu le {formatDate(selected.created_at)}</p>
              <div className="flex gap-2 pt-4" style={{ borderTop: `1px solid ${T.border}` }}>
                <button onClick={() => { window.open(`mailto:${selected.email}?subject=Re: ${selected.subject}`); updateStatus(selected.contact_id, 'replied'); }}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: T.vert, fontFamily: "'Montserrat', sans-serif" }} disabled={updating}>
                  <Reply className="w-4 h-4" /> Repondre
                </button>
                <button onClick={() => { updateStatus(selected.contact_id, 'archived'); setSelected(null); }}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium" style={{ background: '#f5f3ef', color: T.textSecondary }} disabled={updating}>
                  <Archive className="w-4 h-4" /> Archiver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BookingsTab = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => { load(); }, [filter]);
  const load = async () => { setLoading(true); try { setBookings(await adminApi.get(filter === 'all' ? '/api/admin/bookings' : `/api/admin/bookings?status=${filter}`)); } catch (err) { console.error('Failed to load bookings:', err); } setLoading(false); };
  const updateStatus = async (id, status) => { setUpdating(true); try { await adminApi.patch(`/api/admin/bookings/${id}`, { status }); await load(); if (selected?.booking_id === id) setSelected({ ...selected, status }); } catch (err) { console.error('Failed to update booking:', err); } setUpdating(false); };

  const badge = (status) => {
    const m = { pending: { l: 'En attente', c: '#a07c3a', b: 'rgba(196,167,108,0.12)' }, confirmed: { l: 'Confirme', c: '#16a34a', b: 'rgba(22,163,74,0.08)' }, cancelled: { l: 'Annule', c: '#dc2626', b: 'rgba(239,68,68,0.08)' }, completed: { l: 'Termine', c: T.textMuted, b: '#f3f4f6' } };
    const s = m[status] || m.pending;
    return <span className="px-2.5 py-1 text-[10px] font-semibold rounded-full" style={{ backgroundColor: s.b, color: s.c }}>{s.l}</span>;
  };

  const visitLabel = (t) => ({ terrain: 'Visite du Terrain', 'maison-temoin': 'Maison Temoin', complete: 'Visite Complete' }[t] || t);
  const filtered = bookings.filter(b => !search || `${b.first_name} ${b.last_name} ${b.email} ${b.phone}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="bookings-tab">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.textMuted }} />
          <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-11 pr-4 py-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-200" style={INPUT_STYLE} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="px-4 py-2.5 text-[12px] font-medium rounded-xl transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: filter === s ? 'rgba(30,74,26,0.08)' : '#f5f3ef', color: filter === s ? T.vert : T.textMuted }}>
              {s === 'all' ? 'Tous' : s === 'pending' ? 'En attente' : s === 'confirmed' ? 'Confirmes' : s === 'completed' ? 'Termines' : 'Annules'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white" style={CARD}>
          <CalendarDays className="w-10 h-10 mx-auto mb-3" style={{ color: T.border }} />
          <p style={{ color: T.textMuted, fontFamily: "'Montserrat', sans-serif" }}>Aucune reservation</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => (
            <div key={b.booking_id} onClick={() => setSelected(b)}
              className="rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md bg-white" style={CARD}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="font-medium text-sm" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textPrimary }}>{b.first_name} {b.last_name}</p>
                    {badge(b.status)}
                  </div>
                  <div className="flex items-center gap-4 text-[12px]" style={{ color: T.textMuted }}>
                    <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {b.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {b.time}</span>
                    <span>{visitLabel(b.visit_type)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  {b.status === 'pending' && (
                    <>
                      <button onClick={() => updateStatus(b.booking_id, 'confirmed')} className="px-3 py-1.5 rounded-lg text-[11px] font-semibold" style={{ background: 'rgba(22,163,74,0.08)', color: '#16a34a' }} disabled={updating}>Confirmer</button>
                      <button onClick={() => updateStatus(b.booking_id, 'cancelled')} className="px-3 py-1.5 rounded-lg text-[11px] font-semibold" style={{ background: 'rgba(239,68,68,0.08)', color: '#dc2626' }} disabled={updating}>Annuler</button>
                    </>
                  )}
                  {b.status === 'confirmed' && (
                    <button onClick={() => updateStatus(b.booking_id, 'completed')} className="px-3 py-1.5 rounded-lg text-[11px] font-semibold" style={{ background: '#f5f3ef', color: T.textSecondary }} disabled={updating}>Termine</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-2xl overflow-y-auto max-h-[90vh] bg-white" style={{ border: `1px solid ${T.border}`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <div className="p-5 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
              <h2 className="text-base font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Reservation</h2>
              <button onClick={() => setSelected(null)} style={{ color: T.textMuted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">{badge(selected.status)}</div>
              <div className="space-y-2.5">
                <div className="flex items-center gap-3"><User className="w-4 h-4" style={{ color: T.textMuted }} /><span className="text-sm" style={{ color: T.textPrimary }}>{selected.first_name} {selected.last_name}</span></div>
                <div className="flex items-center gap-3"><Mail className="w-4 h-4" style={{ color: T.textMuted }} /><a href={`mailto:${selected.email}`} className="text-sm" style={{ color: T.vert }}>{selected.email}</a></div>
                <div className="flex items-center gap-3"><Phone className="w-4 h-4" style={{ color: T.textMuted }} /><a href={`tel:${selected.phone}`} className="text-sm" style={{ color: T.vert }}>{selected.phone}</a></div>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#f5f3ef' }}>
                <div className="space-y-2">
                  <div className="flex items-center gap-3"><MapPin className="w-4 h-4" style={{ color: T.or }} /><span className="text-sm font-medium" style={{ color: T.textPrimary }}>{visitLabel(selected.visit_type)}</span></div>
                  <div className="flex items-center gap-3"><CalendarDays className="w-4 h-4" style={{ color: T.or }} /><span className="text-sm" style={{ color: T.textSecondary }}>{selected.date}</span></div>
                  <div className="flex items-center gap-3"><Clock className="w-4 h-4" style={{ color: T.or }} /><span className="text-sm" style={{ color: T.textSecondary }}>{selected.time}</span></div>
                </div>
              </div>
              {selected.interest && <div><p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: T.textMuted }}>Interet</p><p className="text-sm" style={{ color: T.textPrimary }}>{selected.interest}</p></div>}
              {selected.message && <div><p className="text-[11px] uppercase tracking-wider mb-1" style={{ color: T.textMuted }}>Message</p><p className="text-sm p-3 rounded-xl" style={{ background: '#f5f3ef', color: T.textPrimary }}>{selected.message}</p></div>}
              <p className="text-[11px]" style={{ color: T.textMuted }}>Cree le {formatDate(selected.created_at)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAudiencePage;
